import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { filter, find, isEmpty, isNull, sumBy, remove } from "lodash";
import { shallow } from "zustand/shallow";

import { getErrorMsg } from "../../../Common/utils/common";
import { bugNotif } from "../../../Common/api/bugsnag";
import { getOngoingGiveaways, getUpcomingGiveaways } from "../../api/giveaway";
import { getWlConfig } from "../../services/config";
import { getUserSubscriptionListStripe } from "../../services/subscription";

import { useUserCancelSubscriptionStore } from "./store";
import { useWebUserProvider } from "../../CommonStores/user/provider";
import { useWebUserStore } from "../../CommonStores/user/users";

import { GLOBAL_QUERY_KEY } from "../../../AppUser/CommonStores";

export const FETCH_USER_POWER_UPS_QUERY_KEY = "FETCH_USER_POWERUPS";
const GLOBAL_SETTINGS_QUERY_KEY = "FETCH_WL_CONFIG";

export const useUserCancelSubscriptionProvider = () => {
  const searchParams = new URLSearchParams(window.location.search);
  const userId = searchParams.get("userId") as string;

  const {
    inValidUserRequest,
    isLoadingProviders: isLoadingWebUser,
    isActiveStripeSubscriptionLoading,
  } = useWebUserProvider({
    userId,
  });

  const [
    setOngoingGiveaway,
    setMajorAndSuperGiveawayList,
    setSubscriptionList,
    setPowerUpList,
    setWLConfig,
  ] = useUserCancelSubscriptionStore(
    (store) => [
      store.setOngoingGiveaway,
      store.setMajorAndSuperGiveawayList,
      store.setSubscriptionList,
      store.setPowerUpList,
      store.setWlConfig,
    ],
    shallow
  );

  const [user] = useWebUserStore((s) => [s.user], shallow);

  const { error: wlConfigError, isLoading: loadingWLConfig } = useQuery({
    queryKey: [GLOBAL_SETTINGS_QUERY_KEY],
    queryFn: async () => {
      const resp = await getWlConfig();
      if (resp) setWLConfig(resp);
      return resp;
    },
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (!isNull(wlConfigError)) {
      bugNotif(
        "useUserCancelSubscriptionProvider - wlConfigError",
        getErrorMsg(wlConfigError).rawErrorMsg
      );
    }
  }, [wlConfigError]);

  const { error: errorSubscriptionList, isLoading: loadingSubscriptionList } =
    useQuery({
      queryKey: [GLOBAL_QUERY_KEY.subscriptionList, user],
      queryFn: async () => {
        if (!user) return null;
        const resp = await getUserSubscriptionListStripe(user);
        if (resp) {
          const subscriptionList = [...resp];
          const powerUps = remove(subscriptionList, (sub) => sub.isOneOff);
          setSubscriptionList(subscriptionList);
          setPowerUpList(powerUps);
        }
        return resp;
      },
      refetchOnWindowFocus: true,
    });

  useEffect(() => {
    if (!isNull(errorSubscriptionList)) {
      bugNotif(
        "useUserCancelSubscriptionProvider - subscriptionList",
        getErrorMsg(errorSubscriptionList).rawErrorMsg
      );
    }
  }, [errorSubscriptionList]);

  const { error: errorOngoing, isLoading: loadingOngoing } = useQuery({
    queryKey: ["GET_ONGOING_GIVEAWAYS_LIST"],
    queryFn: async () => {
      const resp = await getOngoingGiveaways();
      if (resp) {
        const findGiveAway = find(resp.data.result, (g) => !g.closed);
        setOngoingGiveaway(findGiveAway || null);
      }
      return resp;
    },
    refetchOnWindowFocus: true,
  });

  useEffect(() => {
    if (!isNull(errorOngoing)) {
      bugNotif(
        "useUserCancelSubscriptionProvider - ongoingGiveaways",
        getErrorMsg(errorOngoing).rawErrorMsg
      );
    }
  }, [errorOngoing]);

  // Major / Super Giveaways
  const { error: errorMajor, isLoading: loadingMajor } = useQuery({
    queryKey: ["GET_UPCOMING_GIVEAWAYS_LIST"],
    queryFn: async () => {
      const resp = await getUpcomingGiveaways();
      if (resp) setMajorAndSuperGiveawayList(resp.data.result);
      return resp;
    },
    refetchOnWindowFocus: true,
  });

  useEffect(() => {
    if (!isNull(errorMajor)) {
      bugNotif(
        "useUserCancelSubscriptionProvider - majorGiveaways",
        getErrorMsg(errorMajor).rawErrorMsg
      );
    }
  }, [errorMajor]);

  // Combine loading states
  const isLoadingProviders =
    isLoadingWebUser ||
    isActiveStripeSubscriptionLoading ||
    loadingWLConfig ||
    // loadingSubscriptionList ||
    loadingOngoing ||
    loadingMajor;

  return {
    isLoadingProviders,
    inValidUserRequest,
  };
};
